import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Notification } from '../../types';
import NotificationList from './NotificationList';
import { supabase } from '../../lib/supabase';

interface NotificationBellProps {
  notifications: Notification[];
  onMarkAsRead: (notificationId: string) => void;
  onClearAll: () => void;
  user: any;
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
}

export default function NotificationBell({ 
  notifications, 
  onMarkAsRead, 
  onClearAll, 
  user, 
  setNotifications 
}: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const unreadCount = notifications.filter(n => !n.read).length;

  // Real-time subscription for notifications
  useEffect(() => {
    if (!user) return;

    const subscription = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: '*',  // Listen for all events
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Notification change received:', payload);
          
          // Handle deletions
          if (payload.eventType === 'DELETE') {
            setNotifications(prev => 
              prev.filter(n => n.id !== payload.old.id)
            );
          }
          // Handle inserts
          else if (payload.eventType === 'INSERT') {
            const notification = {
              ...payload.new,
              created_at: payload.new.created_at || new Date().toISOString()
            };
            setNotifications(prev => [...prev, notification]);
          }
          // Handle updates
          else if (payload.eventType === 'UPDATE') {
            setNotifications(prev =>
              prev.map(n =>
                n.id === payload.new.id ? { ...n, ...payload.new } : n
              )
            );
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user, setNotifications]);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none"
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg z-50">
          <NotificationList
            notifications={notifications}
            onMarkAsRead={onMarkAsRead}
            onClearAll={onClearAll}
            onClose={() => setIsOpen(false)}
          />
        </div>
      )}
    </div>
  );
}